// The firm's duplicate scan - the whole firm's pairs, or one matter's twins -
// and the merge behind it: a preview of everything the merge would do, then
// the merge carrying the token of that preview - the server accepts nothing
// it did not show.

import { getAuthHeaders } from "./auth";

/** A matter as the scan names it, on the firm list and on a matter's card alike. */
export interface DuplicateCase {
  caseId: string;
  /** The public id, and the only thing the matter route accepts. */
  eId: string | null;
  name: string;
  caseNumber: string | null;
}

export interface CaseTwin extends DuplicateCase {
  /** Server labels: "a-number", "drive-folder", "name-dob", "file-hash". */
  matchedBy: string[];
}

/** A value as the server sends it: days bare, instants ISO, ids as strings. */
export type Wire = string | number | boolean | null;

export interface MergeSummary {
  caseId: string;
  eId: string | null;
  caseNumber: string | null;
  name: string;
  status: string | null;
  practiceArea: string | null;
  caseType: string | null;
  alienNumber: string | null;
  assignedTo: string | null;
  clientId: string | null;
  driveFolderId: string | null;
  createdAt: string | null;
}

export interface MergePortalFacts {
  clientEId: string;
  email: string | null;
  accessEnabled: boolean;
  loginCount: number;
  lastLoginAt: string | null;
  passwordSetAt: string | null;
}

export interface MergePreview {
  keep: MergeSummary;
  merge: MergeSummary;
  blockers: Array<{ code: string; message: string }>;
  warnings: Array<{ code: string; message: string; count?: number }>;
  moves: Array<{ id: string; count: number }>;
  divergence: Array<{ path: string; keep: Wire; merge: Wire }>;
  carryUps: Array<{ path: string; value: Wire }>;
  arrays: Array<{ name: string; keepCount: number; mergeCount: number; wouldAdd: number }>;
  portal: {
    action: "moved" | "disabled" | "none";
    keep: MergePortalFacts | null;
    merge: MergePortalFacts | null;
  };
  drive: { action: "moved" | "cleared" | "left" | "none" };
  previewToken: string;
}

export interface MergeInput {
  keepCaseId: string;
  mergeCaseId: string;
  previewToken: string;
  take: string[];
  arrays: Record<string, boolean>;
  acknowledgedWarnings: string[];
}

export interface MergeOutcome {
  merged: true;
  resumed: boolean;
  keepCaseId: string;
  archivedCaseId: string;
  counts: Record<string, number>;
  portal: { action: "moved" | "disabled" | "none"; needsReinvite: boolean };
  drive: { action: "moved" | "cleared" | "left" | "none" };
  identifiers: { carriedAlienNumber: boolean; retiredEId: string | null };
  picks: {
    fields: string[];
    hearing: string | null;
    arrays: Record<string, number>;
    ignored: boolean;
  };
}

/** The server's refusal, with the code a screen acts on. */
export class MergeRefusedError extends Error {
  status: number;
  code: string | null;
  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function json<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
    throw new MergeRefusedError(
      body?.error ?? `${fallback} (HTTP ${res.status})`,
      res.status,
      body?.code ?? null,
    );
  }
  return (await res.json()) as T;
}

function post(path: string, payload: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
}

export async function fetchCaseTwins(caseId: string): Promise<CaseTwin[]> {
  const res = await fetch(`/api/duplicates/case/${encodeURIComponent(caseId)}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`GET /api/duplicates/case failed: ${res.status}`);
  const body = (await res.json()) as { twins?: CaseTwin[] };
  return body.twins ?? [];
}

/** "These are two different people" - the pair stops being reported. */
export async function dismissDuplicate(caseId1: string, caseId2: string): Promise<void> {
  const res = await post("/api/duplicates/dismiss", { caseId1, caseId2 });
  if (!res.ok) throw new Error(`POST /api/duplicates/dismiss failed: ${res.status}`);
}

export async function fetchMergePreview(
  keepCaseId: string,
  mergeCaseId: string,
): Promise<MergePreview> {
  const res = await post("/api/duplicates/merge/preview", { keepCaseId, mergeCaseId });
  return json<MergePreview>(res, "Could not preview the merge");
}

export async function mergeCases(input: MergeInput): Promise<MergeOutcome> {
  const res = await post("/api/duplicates/merge", input);
  return json<MergeOutcome>(res, "The merge was refused");
}

export interface FirmDuplicatePair {
  case1: DuplicateCase;
  case2: DuplicateCase;
  /** 0-100 */
  confidence: number;
  matchedBy: string[];
  suggestedAction: "auto-merge" | "review";
}

/** One value shared by more matters than a duplicate could be. */
export interface OversizedBucket {
  field: string;
  count: number;
  sample: DuplicateCase[];
}

export interface FirmDuplicates {
  groups: FirmDuplicatePair[];
  total: number;
  oversized: OversizedBucket[];
}

/** The strongest pairs first; a scan that could not run is a rejection, never an empty list. */
export async function fetchFirmDuplicates(limit: number): Promise<FirmDuplicates> {
  const res = await fetch(`/api/duplicates?limit=${limit}`, {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  const body = await json<Partial<FirmDuplicates>>(res, "The duplicate scan could not run");
  return { groups: body.groups ?? [], total: body.total ?? 0, oversized: body.oversized ?? [] };
}
